import { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { resolveClientLocale } from 'lib/i18n/browser';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';

import {
  type ProfileExclusion,
  type ProfileSkill,
  searchLanguageSkills,
} from './profile.api';
import {
  PROFILE_EXCLUSION_TEXT_MAX_LENGTH,
  getExcludedSkillUuids,
  getExclusionKey,
  normalizeSelectedExclusions,
  withoutSkills,
} from './profile-exclusions';

interface ExclusionsAutocompleteProps {
  accessToken: string | null;
  profileSkills?: ProfileSkill[];
  value: ProfileExclusion[];
  onChange: (exclusions: ProfileExclusion[]) => void;
  disabled?: boolean;
}

type ExclusionOption = ProfileExclusion | ProfileSkill;

function getOptionKey(option: ExclusionOption) {
  return 'skill' in option ? getExclusionKey(option) : `skill:${option.uuid}`;
}

export function ExclusionsAutocomplete({
  accessToken,
  profileSkills = [],
  value,
  onChange,
  disabled = false,
}: ExclusionsAutocompleteProps) {
  const locale = useLocale();
  const t = getTranslations(locale).adminInputs;
  const [query, setQuery] = useState('');
  const [foundSkills, setFoundSkills] = useState<ProfileSkill[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const browserLocale = useMemo(() => resolveClientLocale(), []);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery.length) {
      setFoundSkills([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const timerId = window.setTimeout(() => {
      setIsLoading(true);

      searchLanguageSkills(normalizedQuery, browserLocale, accessToken ?? undefined)
        .then((skills) => {
          if (!cancelled) {
            setFoundSkills(skills);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setFoundSkills([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [query, browserLocale, accessToken]);

  const options = useMemo(
    () =>
      withoutSkills(foundSkills, [
        ...profileSkills.map((skill) => skill.uuid),
        ...getExcludedSkillUuids(value),
      ]),
    [foundSkills, profileSkills, value]
  );

  return (
    <Autocomplete<ExclusionOption, true, false, true>
      multiple
      freeSolo
      data-testid="profile-exclusions-autocomplete"
      options={options}
      value={value}
      disabled={disabled}
      loading={isLoading}
      filterOptions={(filteredOptions) => filteredOptions}
      isOptionEqualToValue={(option, selected) => getOptionKey(option) === getOptionKey(selected)}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
      onInputChange={(_, nextQuery, reason) => {
        setQuery(reason === 'input' ? nextQuery : '');
      }}
      onChange={(_, nextValue) => {
        onChange(normalizeSelectedExclusions(nextValue, options));
        setQuery('');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t.searchExclusionsPlaceholder}
          helperText={t.searchExclusionsHelper}
          inputProps={{
            ...params.inputProps,
            maxLength: PROFILE_EXCLUSION_TEXT_MAX_LENGTH,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
